import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Network, RefreshCw } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useOrgUnitTree, useSyncFromKeycloak } from "@/hooks/use-org-units";
import { useIsPlatformAdmin } from "@/lib/auth";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@fox1ck-org/ui";
import { OrgTreePanel } from "@/components/org/OrgTreePanel";
import { OrgUnitDetail } from "@/components/org/OrgUnitDetail";
import { CreateOrgUnitDialog } from "@/components/org/CreateOrgUnitDialog";

export default function OrgStructure() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { data: tree, isLoading: treeLoading, error: treeError } = useOrgUnitTree();
  const [showCreate, setShowCreate] = useState(false);
  const syncFromKC = useSyncFromKeycloak();

  // Surface load errors as a toast so the admin sees failures even if the
  // tree panel is off-screen. The panel itself also renders the error inline.
  useEffect(() => {
    if (treeError) {
      toast.error("Не удалось загрузить оргструктуру", {
        description: treeError instanceof Error ? treeError.message : String(treeError),
      });
    }
  }, [treeError]);

  return (
    <div className="space-y-4">
      <PageHeader title="Команды и структура" description="Команды, участники и роли в приложениях">
        {isPlatformAdmin && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                syncFromKC.mutate(undefined, {
                  onSuccess: (data) => {
                    if (data.created > 0) {
                      toast.success(`Из Keycloak добавлено подразделений: ${data.created}`);
                    } else {
                      toast.info("Новых групп нет, всё синхронизировано");
                    }
                  },
                  onError: () => toast.error("Не удалось синхронизировать с Keycloak"),
                })
              }
              disabled={syncFromKC.isPending}
            >
              <RefreshCw className={`h-4 w-4 ${syncFromKC.isPending ? "animate-spin" : ""}`} />
              Синхронизировать с Keycloak
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" /> Новое подразделение
            </Button>
          </div>
        )}
      </PageHeader>

      <div className="flex gap-4 h-[calc(100vh-180px)]">
        {/* Tree Panel */}
        <div className="w-[280px] shrink-0 border rounded-lg bg-card">
          <OrgTreePanel
            tree={tree ?? []}
            isLoading={treeLoading}
            error={treeError as Error | null}
            selectedId={unitId}
            onSelect={(id) => navigate(`/org-structure/${id}`)}
          />
        </div>

        {/* Detail Panel */}
        <div className="flex-1 min-w-0">
          {unitId ? (
            <OrgUnitDetail unitId={unitId} />
          ) : (
            <div className="flex items-center justify-center h-full border rounded-lg bg-card">
              <EmptyState
                icon={<Network className="h-10 w-10" />}
                title="Выберите подразделение"
                description="Выберите в дереве, чтобы увидеть подробности"
              />
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateOrgUnitDialog tree={tree ?? []} onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}
